import Core                       from "@/core/Core";
import CompanyTaxSystem           from "@/domains/CompanyTaxSystems/CompanyTaxSystem";
import Company                    from "@/domains/Companies/Company";
import CompaniesDAO               from "@/domains/Companies/CompaniesDAO";
import EdiOperatorsDAO            from "@/domains/CompanyTaxSystems/CompanyTaxSystemsDAO";
import CompanyAdditionalAttribute from "@/domains/Companies/CompanyAdditionalAttribute";
import Partner                    from "@/domains/Partners/Partner";
import Dictionary                 from "@/core/types/Dictionary";
import BankAccountsService        from "@/domains/BankAccounts/BankAccountsService";
import ContextMenuItem            from "@/lib/components/VContextMenu/ContextMenuItem";
import VatRate                    from "@/core/types/VatRate";

/**
 * Сервис для работы с компаниями
 */

class CompaniesService {

  /**
   * Кеш систем налогообложения. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static companyTaxSystems = reactive(new Dictionary<number, CompanyTaxSystem>());

  /**
   * Ставки НДС, доступные для Узбекистана.
   *
   * Значения берутся из справочника — он единственный источник. Набор задан
   * списком процентов здесь: справочник плоский, признака страны/режима
   * у ставки нет. Появится признак на беке — фильтр уедет туда.
   */
  static get uzVatRates(): VatRate[] {
    return Core.dictionaries.vatRates.toArray().filter(rate => rate.id === 12);
  }

  /**
   * Ставки НДС для УСН. С 2025 года УСН предполагает собственную ставку,
   * набор отличается от общего списка.
   */
  static get stsVatRates(): VatRate[] {
    return Core.dictionaries.vatRates.toArray().filter(rate => [7, 5, 0].includes(rate.id));
  }

  static countries = [
    {
      'code':       'CN',
      'name':       'Китай',
      'nameFull':   'Китайская Народная Республика',
      'codeAlpha3': 'CHN',
      'codeNum':    156
    },
    {
      'code':       'ES',
      'name':       'Испания',
      'nameFull':   'Королевство Испания',
      'codeAlpha3': 'ESP',
      'codeNum':    724
    },
    {
      'code':       'BY',
      'name':       'Белоруссия',
      'nameFull':   'Республика Беларусь',
      'codeAlpha3': 'BLR',
      'codeNum':    112
    },
    {
      'code':       'IN',
      'name':       'Индия',
      'nameFull':   'Республика Индия',
      'codeAlpha3': 'IND',
      'codeNum':    356
    },
    {
      'code':       'KZ',
      'name':       'Казахстан',
      'nameFull':   'Республика Казахстан',
      'codeAlpha3': 'KAZ',
      'codeNum':    398
    },
    {
      'code':       'PL',
      'name':       'Польша',
      'nameFull':   'Республика Польша',
      'codeAlpha3': 'POL',
      'codeNum':    616
    },
    {
      'code':       'UZ',
      'name':       'Узбекистан',
      'nameFull':   'Республика Узбекистан',
      'codeAlpha3': 'UZB',
      'codeNum':    860
    },
    {
      'code':       'RU',
      'name':       'Россия',
      'nameFull':   'Российская Федерация',
      'codeAlpha3': 'RUS',
      'codeNum':    643
    },
    {
      'code':       'GB',
      'name':       'Великобритания',
      'nameFull':   'Соединённое Королевство Великобритании и Северной Ирландии',
      'codeAlpha3': 'GBR',
      'codeNum':    826
    },
    {
      'code':       'DE',
      'name':       'Германия',
      'nameFull':   'Федеративная Республика Германия',
      'codeAlpha3': 'DEU',
      'codeNum':    276
    },
    {
      'code':       'CH',
      'name':       'Швейцария',
      'nameFull':   'Швейцарская Конфедерация',
      'codeAlpha3': 'CHE',
      'codeNum':    756
    },
    {
      "code": "SM",
      "name": "Сан-Марино",
      "nameFull": "Сан-Марино",
      "codeAlpha3": null,
      "codeNum": null
    }
  ];

  static async get(id: number): Promise<Company | null> {
    const response = await CompaniesDAO.get(id);

    if (!response.ok) {
      return null;
    }

    return Core.utils.isObject(response.data) ? Company.fromServer(response.data) : null;
  }

  static async getList(
    params?: Parameters<typeof CompaniesDAO.getList>[0]
  ): Promise<Company[]> {
    const response = await CompaniesDAO.getList(params);

    return Core.utils.hydrateArray(response.data, Company.fromServer)
  }

  static async getTaxSystemsList(): Promise<CompanyTaxSystem[]> {
    const data = await EdiOperatorsDAO.getList();
    return Core.utils.isNotEmptyArray(data) ? data.map((item: any) => CompanyTaxSystem.fromServer(item)) as CompanyTaxSystem[] : [];
  }

  static async getAdditionalAttributes(countryCode: string): Promise<Record<string, CompanyAdditionalAttribute>> {
    const response = await Core.rpc.makeRequest('companyAttributeTypes.getList', {
      country: { code: countryCode }
    });

    if (response.ok && Core.utils.isNotEmptyArray(response.data)) {
      return Object.fromEntries(
        response.data.map((item: any) => [
          item.alias,
          CompanyAdditionalAttribute.fromServer(item)
        ])
      );
    }

    Core.showNotification.error('Не получилось загрузить список реквизитов для этой страны');
    return {};
  }

  /** ------- CRUD ------- */

  static async add(company: Company, partnerId?: number): Promise<boolean> {
    const response = await CompaniesDAO.add(company, partnerId);

    if (response.ok) {
      Core.showNotification.success('Компания успешно добавлена');
    }

    return response.data;
  }

  static async update(company: Company): Promise<boolean> {
    const response = await CompaniesDAO.update(company);

    if (response.ok) {
      Core.showNotification.success('Компания успешно обновлена');
    }

    return response.data;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await CompaniesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Компания успешно удалена');
    }

    return response.data;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню компании.
   * Старый delete(id) оставлен для прямых вызовов без UX.
   */
  static async deleteWithConfirmation(company: Company): Promise<boolean> {
    // СВЕРИТЬ порядок аргументов Core.confirm(question, title).
    // В старом CompanyContextMenu было Core.confirm('Компания: ...', 'Удалить эту компанию?')
    // — то есть наоборот. Если у Core.confirm первым идёт title — поменять две строки местами.
    const confirmed = await Core.confirm(
      'Удалить эту компанию?',
      `Компания: ${company.name}`,
    );

    if (confirmed === false) {
      return false;
    }
    else {
      return await this.delete(company.id);
    }
  }

  /** ------- открытие диалога создания/редактирования компании ------- */

  static async createCompany(
    partner: Partner,
    onCompanyCreated: (data?: any) => void,
    readOnly = false,
  ) {

    const newCompany = await Company.fromPartner(partner);

    this.showCompanyDialog(newCompany, onCompanyCreated, null, readOnly);
  }

  static editCompany(
    company: Company,
    onCompanyUpdated: (data?: any) => void,
    readOnly = false,
  ) {
    this.showCompanyDialog(company, null, onCompanyUpdated, readOnly);
  }

  static showCompanyDialog(
    company: Company,
    onCompanyCreated: (data?: any) => void,
    onCompanyUpdated: (data?: any) => void,
    readOnly,
  ) {
    Core.dialogs.CompanyCard.show(
      {
        company,
        partner: company.partner,
        readOnly,
      },
      {
        [Company.Events.Created]: onCompanyCreated,
        [Company.Events.Updated]: onCompanyUpdated,
      }
    )
  }

  /** Поиск юр лиц продавца. */
  static searchSellerCompanies(pattern: string): Promise<Company[]> {
    return CompaniesService.getList({ partnerId: undefined, pattern, isSeller: true });   // isSeller=true → фильтр по is_seller
  }

  /** Поиск юр лиц покупателя.
   *  ВНИМАНИЕ: бэк (companies.getList) фильтрует по роли только при isSeller===true.
   *  Ветки для покупателя на бэке нет — false вернёт компании всех партнёров. */
  static searchBuyerCompanies(pattern: string): Promise<Company[]> {
    return CompaniesService.getList({ partnerId: undefined, pattern, isSeller: false});  // де-факто = без фильтра по роли
  }

  /** не самое элегантное решение, надо еще подумать*/

  /** todo: вернуться и найти решение элегантней, требуется вход данных, а не только получение их в меню */

  /**
   * Контекстное меню компании. Фабрика, а не геттер: пункты «Добавить р/с» и
   * «История» завязаны на партнёра (его нет в самой строке-компании), а
   * доступность пунктов-действий — на readOnly-режим. И партнёр, и readOnly,
   * и колбэк обновления приходят из контекста потребителя (секция/диалог),
   * поэтому замыкаются здесь.
   *
   * @param partner     полный партнёр (нужен для createBankAccount)
   *
   * @param onChange    колбэк «список изменился» — дёргается из callback'ов,
   *                    меняющих данные (создание р/с, удаление). Через него
   *                    потребитель обновляет грид / перечитывает партнёра.
   *
   * @param readOnly    режим только-чтение: пункты-действия дизейблятся.
   *
   * @param includeOpen включать ли пункт «Открыть». В гриде секции — true
   *                    (двойной клик не единственный путь). Внутри уже открытой
   *                    карточки — false (иначе пункт откроет второй диалог поверх).
   */
  static getContextMenu(
    partner: Partner,
    onChange: () => void,
    readOnly: boolean = false,
    includeOpen: boolean = true,
  ): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];

    if (includeOpen === true) {
      items.push(
        new ContextMenuItem(
          'Открыть',
          'open_in_new',
          (company: Company) => {
            return CompaniesService.editCompany(company, onChange, readOnly);
          },
          null,
          false,
          false,
          true,
        ),
      );
    }

    items.push(
      new ContextMenuItem(
        'Добавить р/с',
        'post_add',
        (company: Company) => {
          return BankAccountsService.createBankAccount(partner, company, onChange);
        },
        (_company: Company) => {
          if (readOnly === true) {
            return 'Недоступно в режиме просмотра';
          }
          else {
            return null;
          }
        },
        false,
        true,
      ),
    );

    items.push(
      new ContextMenuItem(
        'История',
        'history',
        (company: Company) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'company',
            entityId:       company.id,
            showAddComment: false,
          });
        },
        null,
        false,
        false,
      ),
    );

    items.push(
      new ContextMenuItem(
        'Удалить',
        'delete',
        async (company: Company) => {
          if (await CompaniesService.deleteWithConfirmation(company)) {
            onChange();
          }
        },
        (_company: Company) => {
          if (readOnly === true) {
            return 'Недоступно в режиме просмотра';
          }
          else {
            return null;
          }
        },
        false,
        true,
      ),
    );

    return items;
  }
}

export default CompaniesService;
